/**
 * ScorecardDayDetail — read-only day-detail drawer for the HQ daily
 * scorecard (issue #510).
 *
 * Opened by clicking a store row/card on `/stores/hq`, scoped to that store
 * and the date currently selected in the page's date picker. Side drawer on
 * desktop, full screen on mobile: same responsive shape as the existing
 * GrantEditor.tsx panel (`w-full max-w-md`, backdrop button, Escape to
 * close), so no new drawer chrome is introduced.
 *
 * Data comes from the exact store-scoped hooks Today.tsx already uses for an
 * HQ caller acting on a picked store: useStoreMe for the checklist item
 * labels, useStoreReports for each phase's submitted state. No new API.
 */

import { useEffect } from 'react';
import { Check, X, Camera } from 'lucide-react';
import { formatDate, formatDateTime } from '@/lib/dateUtils';
import {
  useStoreMe,
  useStoreReports,
  PHASE_LABELS,
  STORE_PHASES,
  type StorePhase,
  type PhaseState,
  type ChecklistItemRef,
} from '@/lib/useStores';
import { StorePhotoThumb } from './PhotoCapture';
import { ErrorCard } from './storesShared';

export function ScorecardDayDetail({
  storeNo,
  storeName,
  date,
  onClose,
}: {
  storeNo: number;
  storeName: string;
  date: string;
  onClose: () => void;
}) {
  const meQ = useStoreMe(storeNo);
  const reportsQ = useStoreReports(date, storeNo);

  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const isLoading = meQ.isLoading || reportsQ.isLoading;
  const error = meQ.error ?? reportsQ.error;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      {/* Backdrop: click outside closes. */}
      <button
        type="button"
        aria-label="Close"
        onClick={onClose}
        className="absolute inset-0 bg-black/30"
      />
      {/* Panel: full width on mobile, capped at max-w-md on wider screens (side drawer). */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`${storeName} checklist detail`}
        className="relative z-10 flex h-full w-full max-w-md flex-col bg-amp-white shadow-2 dark:bg-amp-dark-surface"
      >
        <div className="flex items-start justify-between border-b border-amp-line px-5 py-4 dark:border-amp-dark-line">
          <div className="min-w-0">
            <p className="mb-0.5 text-xs uppercase tracking-wide text-amp-olive dark:text-amp-olive-dark">
              {formatDate(date)}
            </p>
            <h2 className="truncate text-lg font-semibold text-amp-ink dark:text-amp-dark-ink">{storeName}</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="shrink-0 text-amp-ink-muted hover:text-amp-ink dark:text-amp-dark-ink-muted dark:hover:text-amp-dark-ink"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-4">
          {isLoading ? (
            <div className="space-y-3">
              {STORE_PHASES.map((p) => (
                <div key={p} className="h-24 animate-pulse rounded-lg bg-amp-cream dark:bg-amp-dark-surface-warm" />
              ))}
            </div>
          ) : error ? (
            <ErrorCard message={error.message} />
          ) : (
            <div className="space-y-5">
              {STORE_PHASES.map((phase) => (
                <PhaseDetailSection
                  key={phase}
                  phase={phase}
                  state={reportsQ.data?.phases[phase]}
                  items={meQ.data?.checklist[phase] ?? []}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function PhaseDetailSection({
  phase,
  state,
  items,
}: {
  phase: StorePhase;
  state: PhaseState | undefined;
  items: ChecklistItemRef[];
}) {
  const submitted = !!state?.submitted;
  const report = state?.report;
  const checkedMap = new Map((report?.checklist ?? []).map((c) => [c.item_id, c.checked]));
  const doneCount = items.filter((i) => checkedMap.get(i.id)).length;

  return (
    <section className="rounded-lg border border-amp-line dark:border-amp-dark-line">
      <div className="flex items-center justify-between border-b border-amp-line px-4 py-2.5 dark:border-amp-dark-line">
        <h3 className="text-[15px] font-semibold text-amp-ink dark:text-amp-dark-ink">{PHASE_LABELS[phase]}</h3>
        {submitted ? (
          <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-green-700 dark:text-green-300">
            <Check className="h-3.5 w-3.5" aria-hidden /> Submitted
          </span>
        ) : (
          <span className="inline-flex items-center gap-1 text-[12px] font-semibold text-amp-ink-muted dark:text-amp-dark-ink-muted">
            <X className="h-3.5 w-3.5" aria-hidden /> Not submitted
          </span>
        )}
      </div>

      {submitted && report ? (
        <div className="space-y-3 px-4 py-3">
          <p className="text-[13px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
            {formatDateTime(report.submitted_at)}
            {report.submitted_by ? ` · ${report.submitted_by}` : ''}
          </p>

          <div className="flex flex-wrap gap-x-4 gap-y-1 text-[13px] text-amp-ink dark:text-amp-dark-ink">
            <span>Traffic: {report.traffic_count != null ? report.traffic_count.toLocaleString() : '--'}</span>
            {phase === 'close' && (
              <span>
                Transactions: {report.transactions != null ? report.transactions.toLocaleString() : '--'}
              </span>
            )}
          </div>

          {report.note && (
            <p className="rounded-md bg-amp-cream-warm px-3 py-2 text-[13px] text-amp-ink dark:bg-amp-dark-surface-warm dark:text-amp-dark-ink">
              {report.note}
            </p>
          )}

          {items.length > 0 && (
            <div>
              <p className="mb-1.5 text-[12px] font-medium text-amp-ink-muted dark:text-amp-dark-ink-muted">
                Checklist — {doneCount} of {items.length} done
              </p>
              <div className="space-y-1.5">
                {items.map((item) => {
                  const checked = !!checkedMap.get(item.id);
                  return (
                    <div
                      key={item.id}
                      className={
                        checked
                          ? 'flex items-center gap-2 rounded-md border border-amp-line px-3 py-2 text-[13px] text-amp-ink dark:border-amp-dark-line dark:text-amp-dark-ink'
                          : 'flex items-center gap-2 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-[13px] font-medium text-red-800 dark:border-red-800 dark:bg-red-900/30 dark:text-red-200'
                      }
                    >
                      {checked ? (
                        <Check className="h-4 w-4 shrink-0 text-green-600 dark:text-green-400" aria-hidden />
                      ) : (
                        <X className="h-4 w-4 shrink-0 text-red-700 dark:text-red-300" aria-hidden />
                      )}
                      {item.label}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {report.photos.length > 0 && (
            <div>
              <p className="mb-1.5 flex items-center gap-1 text-[12px] font-medium text-amp-ink-muted dark:text-amp-dark-ink-muted">
                <Camera className="h-3.5 w-3.5" aria-hidden /> {report.photos.length}{' '}
                {report.photos.length === 1 ? 'photo' : 'photos'}
              </p>
              <div className="grid grid-cols-3 gap-2">
                {report.photos.map((p) => (
                  <StorePhotoThumb key={p.id} photoId={p.id} />
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        <p className="px-4 py-3 text-[13px] text-amp-ink-muted dark:text-amp-dark-ink-muted">
          Not submitted for this date.
        </p>
      )}
    </section>
  );
}
